import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { SERVICE_MENU_BASE } from '../config';
import { useAuth } from './AuthContext';
import { useBrand } from './BrandingContext';
import { useTenant } from './TenantContext';

interface MegaMenuContextType {
  isMenuInitialized: boolean;
  initializeMegaMenu: () => void;
}

const MegaMenuContext = createContext<MegaMenuContextType | undefined>(
  undefined,
);

declare global {
  interface Window {
    DO_MEGA_MENU: boolean;
    InitMenu: (
      agentID: string,
      subscriberCookie: string,
      flag: string,
      canActAs: boolean,
      sorryServer: boolean,
    ) => void;
  }
}

const prefix = SERVICE_MENU_BASE;

export const MegaMenuProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isMenuInitialized, setIsMenuInitialized] = useState(false);
  const initializationInProgress = useRef(false);
  const { session } = useAuth();
  const { isAddEditHost } = useTenant();
  const { branding, applyCustomLogo } = useBrand();

  const loadMegaMenuResources = () => {
    return new Promise<void>((resolve) => {
      if (isAddEditHost() || branding?.customheader || !session) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = `${prefix}/js/brightmlsmenu.js`;
      script.async = true;
      document.body.appendChild(script);

      script.onload = () => {
        resolve();
      };
    });
  };

  const cleanupMegaMenuResources = () => {
    if (isAddEditHost() || branding?.customheader) {
      return;
    }
    const script = document.querySelector(
      `script[src="${prefix}/assets/menu/js/brightmlsmenu.js"]`,
    );
    if (script) document.body.removeChild(script);
  };

  const initializeMegaMenu = async () => {
    if (
      !session?.user.memberMlsId ||
      isMenuInitialized ||
      initializationInProgress.current
    )
      return;

    if (isAddEditHost() || branding?.customheader) {
      setIsMenuInitialized(true);
      return;
    }

    initializationInProgress.current = true;

    try {
      await loadMegaMenuResources();

      const agentID = session?.user.memberMlsId || '';

      window.DO_MEGA_MENU = true;

      if (typeof window.InitMenu === 'function') {
        window.InitMenu(agentID, agentID, 'N', false, true);

        if (branding?.logo?.url && !branding.customheader) {
          applyCustomLogo(
            branding.logo.url,
            branding.logo.altText,
            branding.logo.width,
            branding.logo.height,
          );
        }
      }

      setIsMenuInitialized(true);
    } catch (error) {
      console.error('Failed to initialize mega menu:', error);
    } finally {
      initializationInProgress.current = false;
    }
  };

  useEffect(() => {
    return () => {
      if (isMenuInitialized) {
        cleanupMegaMenuResources();
      }
    };
  }, [isMenuInitialized]);

  return (
    <MegaMenuContext.Provider value={{ isMenuInitialized, initializeMegaMenu }}>
      {children}
    </MegaMenuContext.Provider>
  );
};

export const useMegaMenu = () => {
  const context = useContext(MegaMenuContext);
  if (context === undefined) {
    throw new Error('useMegaMenu must be used within a MegaMenuProvider');
  }
  return context;
};
